import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface Client {
  name: string;
  email: string;
  applications: any[];
  leaseApplications: any[];
}

interface ClientAuthContextType {
  isAuthenticated: boolean;
  client: Client | null;
  login: (name: string, email: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const ClientAuthContext = createContext<ClientAuthContextType | undefined>(undefined);

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if client is logged in on app start
    const token = localStorage.getItem('clientToken');
    if (token) {
      // Verify token with backend
      apiService.getClientDashboard()
        .then(response => {
          if (response.status === 'success' && response.data?.client) {
            setIsAuthenticated(true);
            setClient(response.data.client);
          } else {
            localStorage.removeItem('clientToken');
          }
        })
        .catch(() => {
          localStorage.removeItem('clientToken');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (name: string, email: string): Promise<boolean> => {
    try {
      const response = await apiService.clientLogin(name, email);
      if (response.status === 'success' && response.data?.client) {
        setIsAuthenticated(true);
        setClient(response.data.client);
        localStorage.setItem('clientToken', response.data.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Client login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      // No logout endpoint needed for client
    } catch (error) {
      console.error('Client logout error:', error);
    } finally {
      localStorage.removeItem('clientToken');
      setIsAuthenticated(false);
      setClient(null);
    }
  };

  return (
    <ClientAuthContext.Provider value={{
      isAuthenticated,
      client,
      login,
      logout,
      loading
    }}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  const context = useContext(ClientAuthContext);
  if (context === undefined) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
}
